import React, { useEffect, useState } from 'react';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { X, Mail, MousePointerClick, AlertTriangle, MailCheck, Ban, Loader2 } from 'lucide-react';
import { fromApi } from './kit/time';
import LongTextCell from '@/components/LongTextCell';

const EVENT_ICONS = {
  delivered: { icon: MailCheck, color: '#8DC63F', label: 'Delivered' },
  open:      { icon: Mail, color: '#3b82f6', label: 'Open' },
  click:     { icon: MousePointerClick, color: '#6366f1', label: 'Click' },
  bounce:    { icon: AlertTriangle, color: '#ef4444', label: 'Bounce' },
  dropped:   { icon: AlertTriangle, color: '#ef4444', label: 'Dropped' },
  blocked:   { icon: Ban, color: '#ef4444', label: 'Blocked' },
  deferred:  { icon: Loader2, color: '#f59e0b', label: 'Deferred' },
  unsubscribe:{ icon: Ban, color: '#94a3b8', label: 'Unsubscribe' },
  spamreport:{ icon: AlertTriangle, color: '#dc2626', label: 'Spam Report' },
  processed: { icon: MailCheck, color: '#64748b', label: 'Processed' },
};

const fmtTime = (ts) => {
  if (!ts) return '—';
  // Mailgun webhook `timestamp` is unix seconds; our received_at is ISO.
  const d = typeof ts === 'number' ? new Date(ts * 1000) : fromApi(ts);
  if (isNaN(d)) return '—';
  return d.toLocaleString();
};

export default function CampaignDrillDown({ campaignId, label, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!campaignId) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/email/analytics/${encodeURIComponent(campaignId)}/events`);
        setEvents(data);
      } catch (e) {
        toast({ title: 'Failed to load events', description: e.response?.data?.detail || 'Try again' });
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId]);

  if (!campaignId) return null;

  const filtered = filter === 'all' ? events : events.filter((e) => e.event === filter);

  // Count each event type for the filter chips
  const counts = events.reduce((acc, e) => {
    acc[e.event] = (acc[e.event] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="drilldown-overlay">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} data-testid="drilldown-backdrop" />

      {/* Drawer */}
      <div className="relative w-full max-w-3xl bg-white shadow-2xl overflow-hidden flex flex-col animate-in slide-in-from-right" data-testid="drilldown-drawer">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60">Campaign Drill-Down</div>
            <div className="font-serif text-xl font-bold text-white" title={campaignId}>{label || campaignId}</div>
            <div className="text-[11px] text-white/70 mt-0.5">Most recent {events.length} events</div>
          </div>
          <button onClick={onClose} data-testid="drilldown-close" className="p-2 rounded-lg text-white/70 hover:bg-white/10 hover:text-white">
            <X size={16} />
          </button>
        </div>

        {/* Filter chips */}
        <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap gap-1.5">
          <button
            onClick={() => setFilter('all')}
            data-testid="drilldown-filter-all"
            className="text-[11px] font-semibold px-2.5 py-1 rounded-md border transition"
            style={filter === 'all' ? { background: '#070B5F', color: 'white', borderColor: '#070B5F' } : { background: 'white', color: '#070B5F', borderColor: '#e2e8f0' }}
          >All ({events.length})</button>
          {Object.entries(counts).map(([evt, n]) => {
            const meta = EVENT_ICONS[evt] || { color: '#64748b', label: evt };
            const active = filter === evt;
            return (
              <button
                key={evt}
                onClick={() => setFilter(evt)}
                data-testid={`drilldown-filter-${evt}`}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-md border transition"
                style={active ? { background: meta.color, color: 'white', borderColor: meta.color } : { background: 'white', color: meta.color, borderColor: meta.color + '50' }}
              >{meta.label} ({n})</button>
            );
          })}
        </div>

        {/* Events table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 flex items-center justify-center gap-2 text-slate-500 text-sm"><Loader2 size={14} className="animate-spin" /> Loading events…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="drilldown-empty">
              No events {filter !== 'all' ? `of type "${filter}"` : ''} yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0">
                <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Event</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Recipient</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Detail</th>
                  <th className="text-right px-3 py-2.5 font-semibold">When</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e, i) => {
                  const meta = EVENT_ICONS[e.event] || { icon: Mail, color: '#64748b', label: e.event };
                  const Icon = meta.icon;
                  return (
                    <tr key={e.event_id || i} className="hover:bg-slate-50/60" data-testid={`drilldown-event-row-${i}`}>
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: meta.color + '18', color: meta.color }}>
                          <Icon size={10} /> {meta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700 text-[12px] font-mono">
                        <LongTextCell id={`drilldown-email-${i}`} text={e.email || '—'} lines={1} maxWidth={180} />
                      </td>
                      <td className="px-3 py-2.5 text-[11px] text-slate-500">
                        <LongTextCell id={`drilldown-detail-${i}`} text={e.url || e.reason || ''} lines={2} maxWidth={280}>
                          {e.event === 'click' && e.url && (
                            <a href={e.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: '#6366f1' }}>{e.url}</a>
                          )}
                          {(e.event === 'bounce' || e.event === 'dropped' || e.event === 'blocked') && e.reason && (
                            <span className="text-red-600">{e.reason}</span>
                          )}
                          {!e.url && !e.reason && '—'}
                        </LongTextCell>
                      </td>
                      <td className="px-3 py-2.5 text-right text-[11px] text-slate-500 whitespace-nowrap">{fmtTime(e.received_at || e.timestamp)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
          Showing the most recent 200 events for this campaign. Older events remain in the database.
        </div>
      </div>
    </div>
  );
}
